import { computed } from '@angular/core';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';

import type { IProfile } from '@rpgc-ai/shared-interfaces/interfaces/profile.interface';
import type { ISocialLink } from '@rpgc-ai/shared-interfaces/interfaces/public-profile.interface';

import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

import { initialProfile, profileState } from './profile.model';

import { withServerSync } from '../utils/server-sync';

export const ProfileStore = signalStore(
  { providedIn: 'root' },

  // Redux DevTools поддержка
  withDevtools('profile'),

  // Базовое состояние стора с profile
  withState(profileState),

  // Синхронизация profile с сервером (используем другой ключ чтобы избежать конфликта)
  withServerSync<IProfile>({
    key: 'syncedProfile',
    initialValue: initialProfile,
    syncOnInit: true,
  }),

  withComputed((store) => {
    return {
      isAuthenticated: computed<boolean>(() => !!store.profile()),
      userName: computed<string>(() => store.profile()?.name ?? ''),
      userDefaultPersonaId: computed<string>(() => store.profile()?.defaultPersonaId ?? ''),
      userEmail: computed<string>(() => store.profile()?.email ?? ''),
      userNsfwFilter: computed<boolean>(() => store.profile()?.nsfwFilter ?? true),
      userHasAds: computed<boolean>(() => store.profile()?.hasAds ?? true),
      chatImageCost: computed<number>(() => store.profile()?.imageGenerationLimits?.chat?.estimatedCostTokens ?? 0),
      backgroundImageCost: computed<number>(() => store.profile()?.imageGenerationLimits?.characterBackground?.estimatedCostTokens ?? 0),
    };
  }),

  withMethods((store) => {
    return {
      /**
       * Обновление профиля с синхронизацией
       */
      setProfile(profile: IProfile): void {
        // Обновляем локальное состояние
        updateState(store, '[Profile] Profile: Set', { profile, isLoading: false, error: null });
        // Синхронизируем с сервером
        store['updateSyncedProfile'](profile);
      },

      /**
       * Обновление фильтра NSFW с синхронизацией
       */
      setUserNsfwFilter(nsfwFilter: boolean): void {
        const currentProfile = store.profile();
        const updatedProfile = { ...currentProfile, nsfwFilter };
        updateState(store, '[Profile] NSFW Filter: Set', {
          profile: updatedProfile,
          isLoading: false,
          error: null,
        });
        // Синхронизируем с сервером
        store['updateSyncedProfile'](updatedProfile);
      },
      // setImageGenerationSettings
      /**
       * Установка email
       **/
      setEmail(email: string): void {
        const currentProfile = store.profile();
        if (currentProfile?.email === email) {
          return;
        }
        const updatedProfile = { ...currentProfile, email };
        updateState(store, '[Profile] Email: Set', {
          profile: updatedProfile,
          isLoading: false,
          error: null,
        });
        // Синхронизируем с сервером
        store['updateSyncedProfile'](updatedProfile);
      },

      /**
       * Очистка профиля
       */
      clearProfile(): void {
        updateState(store, '[Profile] Profile: Clear', { profile: initialProfile, isLoading: false, error: null });
        store['updateSyncedProfile'](initialProfile);
      },

      /**
       * Установка состояния загрузки
       */
      setLoading(isLoading: boolean): void {
        updateState(store, '[Profile] Loading: Set', { isLoading });
      },

      /**
       * Установка ошибки
       */
      setError(error: string): void {
        updateState(store, '[Profile] Error: Set', { error, isLoading: false });
      },
      /**
       * Метод для принудительной синхронизации с сервера
       */
      syncProfile(): IProfile | null {
        const syncedProfile = store['syncSyncedProfileFromServer']() as IProfile;
        if (syncedProfile && typeof syncedProfile === 'object' && 'id' in syncedProfile) {
          updateState(store, '[Profile] Profile: Sync', { profile: syncedProfile });
          return syncedProfile;
        }
        return null;
      },

      setDefaultPersonaId(personaId: string): void {
        const currentProfile = store.profile();
        if (currentProfile?.defaultPersonaId === personaId) {
          return;
        }
        const updatedProfile = { ...currentProfile, defaultPersonaId: personaId };
        updateState(store, '[Profile] Default Persona Id: Set', {
          profile: updatedProfile,
          isLoading: false,
          error: null,
        });
      },

      setSocialLinks(socialLinks: ISocialLink[]): void {
        const currentProfile = store.profile();
        if (!currentProfile) {
          return;
        }
        const updatedProfile = { ...currentProfile, socialLinks };
        updateState(store, '[Profile] Social Links: Set', {
          profile: updatedProfile,
          isLoading: false,
          error: null,
        });
      },

      removeConnectedAccount(provider: string): void {
        const profile = store.profile();
        const accounts = profile?.connectedAccounts ?? [];
        if (!accounts.length || !accounts) {
          return;
        }
        const updatedAccounts = accounts.filter((acc) => acc.provider !== provider);
        updateState(store, '[Profile] Connected Social Account: Disconnect', {
          profile: { ...profile, connectedAccounts: updatedAccounts },
          isLoading: false,
          error: null,
        });
      },
    };
  }),
);
